var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Typography, Divider, Grid, TextField, Button, CircularProgress, Snackbar, Alert, useTheme, Container, Paper, Tooltip, IconButton, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Select, MenuItem, InputLabel, FormControl, } from '@mui/material';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { PasswordField } from '../../auth/components/PasswordField';
import { logout } from '../../auth/redux/authSlice';
import { useServerValidation } from '../../shared/hooks/useServerValidation';
import { changeEmailAsync, changePasswordAsync, exportAccountDataAsync, deleteAccountAsync, resendVerificationEmailAsync, } from '../redux/accountSlice';
export const AccountManagementPage = () => {
    var _a, _b, _c, _d;
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const theme = useTheme();
    const authState = useSelector((state) => state.auth);
    const [newEmail, setNewEmail] = useState('');
    const [currentPassword, setCurrentPassword] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmNewPassword, setConfirmNewPassword] = useState('');
    const [isEmailTyped, setIsEmailTyped] = useState(false);
    const [exportFormat, setExportFormat] = useState('json');
    const [snackbar, setSnackbar] = useState({
        open: false,
        message: '',
        severity: 'success',
    });
    const [openDeleteModal, setOpenDeleteModal] = useState(false);
    const [isUpdatingEmail, setIsUpdatingEmail] = useState(false);
    const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
    const [isDownloadingData, setIsDownloadingData] = useState(false);
    const [isDeletingAccount, setIsDeletingAccount] = useState(false);
    const { errors, setErrors, clearError, clearAllErrors } = useServerValidation();
    const [deleteConfirmation, setDeleteConfirmation] = useState('');
    const handleSnackbarClose = () => {
        setSnackbar(Object.assign(Object.assign({}, snackbar), { open: false }));
    };
    const handleOpenDeleteModal = () => {
        setOpenDeleteModal(true);
    };
    const handleCloseDeleteModal = () => {
        setOpenDeleteModal(false);
    };
    const handleNewPasswordChange = (e) => {
        const value = e.target.value;
        setNewPassword(value);
        if (confirmNewPassword && value !== confirmNewPassword) {
            setErrors([
                { path: 'confirmNewPassword', msg: 'Passwords do not match.' },
            ]);
        }
        else {
            clearError('confirmNewPassword');
        }
    };
    const handleConfirmNewPasswordChange = (e) => {
        const value = e.target.value;
        setConfirmNewPassword(value);
        if (newPassword && value !== newPassword) {
            setErrors([
                { path: 'confirmNewPassword', msg: 'Passwords do not match.' },
            ]);
        }
        else {
            clearError('confirmNewPassword');
        }
    };
    const handleChangeEmail = (e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        clearAllErrors();
        setIsUpdatingEmail(true);
        try {
            yield dispatch(changeEmailAsync({ newEmail })).unwrap();
            setSnackbar({
                open: true,
                message: 'Check your inbox and follow the verification link you will receive.',
                severity: 'success',
            });
            setNewEmail('');
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (error) {
            if (error === null || error === void 0 ? void 0 : error.errors) {
                setErrors(error.errors);
            }
            else {
                setSnackbar({
                    open: true,
                    message: error.message || 'Failed to update email.',
                    severity: 'error',
                });
            }
        }
        finally {
            setIsUpdatingEmail(false);
        }
    });
    const handleResendVerificationEmail = () => __awaiter(void 0, void 0, void 0, function* () {
        try {
            yield dispatch(resendVerificationEmailAsync()).unwrap();
            setSnackbar({
                open: true,
                message: 'Verification email sent successfully.',
                severity: 'success',
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (error) {
            setSnackbar({
                open: true,
                message: error.message || 'Failed to send verification email.',
                severity: 'error',
            });
        }
    });
    const handleChangePassword = (e) => __awaiter(void 0, void 0, void 0, function* () {
        e.preventDefault();
        clearAllErrors();
        if (!currentPassword || !newPassword || !confirmNewPassword) {
            setSnackbar({
                open: true,
                message: 'Please fill in all password fields.',
                severity: 'error',
            });
            return;
        }
        if (errors.confirmNewPassword) {
            setSnackbar({
                open: true,
                message: 'Please fix the errors before submitting.',
                severity: 'error',
            });
            return;
        }
        setIsUpdatingPassword(true);
        try {
            yield dispatch(changePasswordAsync({ currentPassword, newPassword })).unwrap();
            setSnackbar({
                open: true,
                message: 'Password changed successfully.',
                severity: 'success',
            });
            setCurrentPassword('');
            setNewPassword('');
            setConfirmNewPassword('');
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (error) {
            if (error === null || error === void 0 ? void 0 : error.errors) {
                setErrors(error.errors);
            }
            else {
                setSnackbar({
                    open: true,
                    message: error.message || 'Failed to change password.',
                    severity: 'error',
                });
            }
        }
        finally {
            setIsUpdatingPassword(false);
        }
    });
    const handleExportAccountData = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsDownloadingData(true);
        try {
            const format = exportFormat.toLowerCase();
            const response = yield dispatch(exportAccountDataAsync({ format })).unwrap();
            let blob;
            let fileName;
            blob = response;
            fileName = `account_data_${exportFormat}.zip`;
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
            setSnackbar({
                open: true,
                message: 'User data downloaded successfully.',
                severity: 'success',
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (error) {
            setSnackbar({
                open: true,
                message: typeof error === 'string'
                    ? error
                    : 'Failed to download user data.',
                severity: 'error',
            });
        }
        finally {
            setIsDownloadingData(false);
        }
    });
    const handleDeleteAccount = () => __awaiter(void 0, void 0, void 0, function* () {
        setIsDeletingAccount(true);
        try {
            yield dispatch(deleteAccountAsync()).unwrap();
            dispatch(logout());
            navigate('/logout');
            setSnackbar({
                open: true,
                message: 'Account deleted successfully.',
                severity: 'success',
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
        }
        catch (error) {
            setSnackbar({
                open: true,
                message: error.message || 'Failed to delete account.',
                severity: 'error',
            });
        }
        finally {
            setIsDeletingAccount(false);
        }
    });
    return (_jsxs(Container, Object.assign({ sx: {
            width: '100%',
            minHeight: 'calc(100vh - 64px)',
            paddingTop: {
                xs: 'calc(32px + 56px)',
                md: 'calc(32px + 64px)',
            },
            paddingBottom: '32px',
            px: { xs: 6, sm: 0 },
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
        } }, { children: [_jsxs(Paper, Object.assign({ elevation: 3, sx: {
                    width: '100%',
                    maxWidth: 800,
                    padding: 4,
                    margin: 'auto',
                } }, { children: [_jsxs(Box, Object.assign({ sx: { mb: 4 } }, { children: [_jsx(Typography, Object.assign({ variant: "h5", gutterBottom: true }, { children: "Account Information" })), _jsxs(Typography, { children: [_jsx("strong", { children: "Username:" }), " ", (_a = authState.user) === null || _a === void 0 ? void 0 : _a.username] }), _jsxs(Typography, Object.assign({ display: "flex", alignItems: "center", sx: { height: '2em', overflow: 'hidden' } }, { children: [_jsx("strong", { children: "Email:" }), " \u00A0", _jsxs(Box, Object.assign({ component: "span", sx: {
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'flex-start',
                                            flexGrow: 1,
                                            minWidth: 0,
                                        } }, { children: [_jsx(Box, Object.assign({ component: "span", sx: {
                                                    textOverflow: 'ellipsis',
                                                    overflow: 'hidden',
                                                    whiteSpace: 'nowrap',
                                                    flexGrow: { xs: 1, sm: 0 },
                                                    minWidth: 0,
                                                } }, { children: ((_b = authState.user) === null || _b === void 0 ? void 0 : _b.pendingEmail) ||
                                                    ((_c = authState.user) === null || _c === void 0 ? void 0 : _c.email) })), ((_d = authState.user) === null || _d === void 0 ? void 0 : _d.emailVerified) ? (_jsx(Tooltip, Object.assign({ title: "Your email is verified", "aria-label": "Email verified" }, { children: _jsx(CheckCircleIcon, { color: "success", sx: { ml: 1, flexShrink: 0 } }) }))) : (_jsx(Tooltip, Object.assign({ title: "Your email is not verified. Click to resend verification email.", "aria-label": "Email not verified" }, { children: _jsx(IconButton, Object.assign({ color: "warning", onClick: handleResendVerificationEmail, "aria-label": "Resend verification email", sx: { ml: 1, flexShrink: 0 } }, { children: _jsx(WarningIcon, {}) })) })))] }))] }))] })), _jsx(Divider, { sx: { mb: 4 } }), _jsxs(Box, Object.assign({ sx: { mb: 4 } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", gutterBottom: true, sx: { mb: 4 } }, { children: "Change Email" })), _jsx("form", Object.assign({ onSubmit: handleChangeEmail }, { children: _jsxs(Grid, Object.assign({ container: true, spacing: 2 }, { children: [_jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(TextField, { label: "New Email", type: "email", value: newEmail, onChange: (e) => {
                                                    setNewEmail(e.target.value);
                                                    setIsEmailTyped(e.target.value ? true : false);
                                                    clearError('newEmail');
                                                }, required: true, fullWidth: true, error: !!errors.newEmail, helperText: errors.newEmail }) })), isEmailTyped && (_jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsxs(Alert, Object.assign({ severity: "info", icon: _jsx(InfoIcon, {}), sx: { mb: 2 } }, { children: ["Please note: Once your email is updated, you will receive a verification link. You will continue to receive communications in your", ' ', _jsx("b", { children: "current email" }), " until the new one is verified."] })) }))), _jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(Button, Object.assign({ type: "submit", variant: "contained", color: "primary", disabled: isUpdatingEmail, startIcon: isUpdatingEmail && (_jsx(CircularProgress, { size: 20, color: "inherit" })), "data-testid": "button-change-email" }, { children: isUpdatingEmail
                                                    ? 'Updating...'
                                                    : 'Update Email' })) }))] })) }))] })), _jsx(Divider, { sx: { mb: 4 } }), _jsxs(Box, Object.assign({ sx: { mb: 4 } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", gutterBottom: true }, { children: "Change Password" })), _jsx("form", Object.assign({ onSubmit: handleChangePassword }, { children: _jsxs(Grid, Object.assign({ container: true, spacing: 2 }, { children: [_jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(PasswordField, { label: "Current Password", value: currentPassword, id: "current-password", testId: "input-current-password", onChange: (e) => {
                                                    setCurrentPassword(e.target.value);
                                                    clearError('currentPassword');
                                                }, required: true, error: !!errors.currentPassword, helperText: errors.currentPassword }) })), _jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(PasswordField, { id: "new-password", testId: "input-new-password", label: "New Password", value: newPassword, onChange: handleNewPasswordChange, required: true, error: !!errors.newPassword, helperText: errors.newPassword }) })), _jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(PasswordField, { id: "confirm-new-password", testId: "input-confirm-new-password", label: "Confirm New Password", value: confirmNewPassword, onChange: handleConfirmNewPasswordChange, required: true, error: !!errors.confirmNewPassword, helperText: errors.confirmNewPassword }) })), _jsx(Grid, Object.assign({ item: true, xs: 12 }, { children: _jsx(Button, Object.assign({ type: "submit", variant: "contained", color: "primary", disabled: isUpdatingPassword ||
                                                    !!errors.confirmNewPassword, startIcon: isUpdatingPassword && (_jsx(CircularProgress, { size: 20, color: "inherit" })), "data-testid": "button-change-password" }, { children: isUpdatingPassword
                                                    ? 'Updating...'
                                                    : 'Update Password' })) }))] })) }))] })), _jsx(Divider, { sx: { mb: 4 } }), _jsxs(Box, Object.assign({ sx: { mb: 4 } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", gutterBottom: true }, { children: "Download User Data" })), _jsx(Typography, Object.assign({ sx: { mb: 4 } }, { children: "Choose your desired export format. Your account data will be downloaded." })), _jsxs(Grid, Object.assign({ container: true, spacing: 2, alignItems: "end" }, { children: [_jsx(Grid, Object.assign({ item: true, xs: 12, sm: 4 }, { children: _jsxs(FormControl, Object.assign({ fullWidth: true }, { children: [_jsx(InputLabel, Object.assign({ id: "export-format-label" }, { children: "Export Format" })), _jsxs(Select, Object.assign({ labelId: "export-format-label", id: "export-format-select", value: exportFormat, label: "Export Format", onChange: (e) => setExportFormat(e.target
                                                        .value) }, { children: [_jsx(MenuItem, Object.assign({ value: 'json' }, { children: "JSON" })), _jsx(MenuItem, Object.assign({ value: 'csv' }, { children: "CSV" }))] }))] })) })), _jsx(Grid, Object.assign({ item: true, xs: 12, sm: 8 }, { children: _jsx(Button, Object.assign({ variant: "contained", color: "secondary", onClick: handleExportAccountData, disabled: isDownloadingData, startIcon: isDownloadingData && (_jsx(CircularProgress, { size: 20, color: "inherit" })), "data-testid": "button-download-account-data" }, { children: isDownloadingData
                                                ? 'Downloading...'
                                                : 'Download Data' })) }))] }))] })), _jsx(Divider, { sx: { mb: 4 } }), _jsxs(Box, Object.assign({ sx: { mb: 4 } }, { children: [_jsx(Typography, Object.assign({ variant: "h6", gutterBottom: true }, { children: "Delete Account" })), _jsx(Grid, Object.assign({ container: true, spacing: 2, direction: { xs: 'column', sm: 'row' }, alignItems: { sm: 'flex-start' } }, { children: _jsx(Grid, Object.assign({ item: true, xs: 12, sm: "auto" }, { children: _jsx(Button, Object.assign({ variant: "contained", color: "error", onClick: handleOpenDeleteModal, disabled: isDeletingAccount, startIcon: isDeletingAccount && (_jsx(CircularProgress, { size: 20, color: "inherit" })), "data-testid": "button-delete-account" }, { children: isDeletingAccount
                                            ? 'Deleting...'
                                            : 'Delete Account' })) })) }))] })), _jsxs(Dialog, Object.assign({ open: openDeleteModal, onClose: handleCloseDeleteModal, "aria-labelledby": "delete-account-dialog-title", "aria-describedby": "delete-account-dialog-description" }, { children: [_jsx(DialogTitle, Object.assign({ id: "delete-account-dialog-title" }, { children: "Confirm Account Deletion" })), _jsxs(DialogContent, { children: [_jsxs(DialogContentText, Object.assign({ id: "delete-account-dialog-description" }, { children: ["To confirm the deletion of your account, please type", ' ', _jsx("strong", { children: "delete" }), " in the field below."] })), _jsx(Typography, Object.assign({ variant: "body1", color: "error" }, { children: _jsx("strong", { children: "This action cannot be undone." }) })), _jsx(Box, Object.assign({ mt: 2 }, { children: _jsx(TextField, { margin: "dense", id: "delete-confirmation", label: 'Type "delete" to confirm', type: "text", fullWidth: true, variant: "outlined", value: deleteConfirmation, onChange: (e) => setDeleteConfirmation(e.target.value), inputProps: {
                                                'data-testid': 'input-confirm-delete',
                                            } }) }))] }), _jsxs(DialogActions, { children: [_jsx(Button, Object.assign({ onClick: handleCloseDeleteModal, color: "primary" }, { children: "Cancel" })), _jsx(Button, Object.assign({ onClick: () => {
                                            handleDeleteAccount();
                                            handleCloseDeleteModal();
                                        }, color: "error", variant: "contained", disabled: deleteConfirmation.toLowerCase() !== 'delete' }, { children: "Delete" }))] })] }))] })), _jsx(Snackbar, Object.assign({ open: snackbar.open, autoHideDuration: 6000, onClose: handleSnackbarClose, anchorOrigin: { vertical: 'bottom', horizontal: 'center' }, "data-testid": "snackbar" }, { children: _jsx(Alert, Object.assign({ onClose: handleSnackbarClose, severity: snackbar.severity, sx: { width: '100%' } }, { children: snackbar.message })) }))] })));
};
